import { useEffect, useState, useCallback } from "react";
import { Ticket, Loader2, AlertCircle } from "lucide-react";
import * as events from "@/api/events";
import type { EventSummary } from "@/api/events";
import type { WaitlistEntry, MyPasses, MatchesAttending } from "@/api/events";

// ═══════════════════════════════════════════════════════════════════
// Seats — the seeded events, the queue, and what you already hold.
//
// One page because the three are the same question asked at different moments:
// what is on, am I in, and did I get a place. Reading them apart is how you
// conclude the queue is broken when in fact you already hold a ticket.
//
// The queue rules this surface exists to make visible:
//   · `position` is COUNTED per request, never stored — so it moves on its own
//     when someone ahead leaves.
//   · `position` is NULL while you hold an offer. You are out of the queue and
//     deciding, not at a number.
//   · `seats` is per PLACE, not per person: a family of four is one entry.
//   · An event with no capacity has no queue, and says so rather than
//     inventing a number.
// ═══════════════════════════════════════════════════════════════════

export default function SeatsLab() {
  const [list, setList] = useState<EventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        // The list endpoint nests: { data: { events, meta } }. Reading it as a
        // bare array is the mistake the type-checker caught here.
        const res = await events.listEvents({ limit: 30 });
        setList(res.data.events ?? []);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not load events");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const capped = list.filter((e) => (e as { maxCapacity?: number | null }).maxCapacity);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
          <Ticket className="w-6 h-6 text-orange-600" /> Seats
        </h1>
        <p className="text-sm text-gray-600">
          The seeded events, with the queue and your passes on each. Positions are{" "}
          <strong>counted per request, never stored</strong> — which is why they move on their own
          when somebody ahead of you leaves.
        </p>
      </header>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500">
            {list.length} events seeded · {capped.length} have a capacity, so only those can have a
            queue at all.
          </p>
          <div className="space-y-3">
            {list.map((e) => (
              <EventRow key={e.id} event={e} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EventRow({ event }: { event: EventSummary }) {
  const [open, setOpen] = useState(false);
  const cap = (event as { maxCapacity?: number | null }).maxCapacity ?? null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-4 flex items-start justify-between gap-4"
      >
        <div className="min-w-0">
          <div className="font-medium truncate">{event.title}</div>
          <div className="text-sm text-gray-600 mt-0.5">
            {new Date(event.eventDate).toLocaleDateString()} · {event.city}
            {cap ? ` · capacity ${cap}` : " · no capacity limit"}
          </div>
        </div>
        <span className="text-xs text-gray-400 shrink-0">{open ? "hide" : "seats"}</span>
      </button>
      {open && <Seats eventId={event.id} hasCapacity={Boolean(cap)} />}
    </div>
  );
}

function Seats({ eventId, hasCapacity }: { eventId: string; hasCapacity: boolean }) {
  const [entry, setEntry] = useState<WaitlistEntry | null>(null);
  const [passes, setPasses] = useState<MyPasses | null>(null);
  const [watching, setWatching] = useState<boolean | null>(null);
  const [remindMe, setRemindMe] = useState(false);
  const [room, setRoom] = useState<{ going: number; interested: number } | null>(null);
  const [matches, setMatches] = useState<MatchesAttending | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      // Everything about MY relationship to this event, read together.
      // `matches-attending` is allowed to fail on its own — it is the one read
      // here that depends on having an identity board, and a member without
      // one should still see their seat.
      const [w, p, r] = await Promise.all([
        events.myWaitlistEntry(eventId),
        events.myPasses(eventId),
        events.listEventRsvps(eventId, { limit: 100 }),
      ]);
      setEntry(w.data);
      setPasses(p.data);
      const rsvps = r.data.rsvps ?? [];
      setRoom({
        going: rsvps.filter((x) => x.status === "going").length,
        interested: rsvps.filter((x) => x.status === "interested").length,
      });
      try {
        const m = await events.getMatchesAttending(eventId);
        setMatches(m.data);
      } catch {
        setMatches(null);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not read your seat");
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      await fn();
      setNote(ok);
      await load();
    } catch (e) {
      // NO_CAPACITY_LIMIT and ALREADY_GOING land here — the refusals are the
      // feature, so show the server's own words.
      setErr(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-3 text-sm">
      {/* What you already hold */}
      <div>
        <span className="text-gray-500">You hold: </span>
        {passes?.rsvpStatus || (passes?.passCount ?? 0) > 0 ? (
          <>
            {passes?.rsvpStatus && <strong>{passes.rsvpStatus}</strong>}
            {(passes?.passCount ?? 0) > 0 && (
              <>
                {passes?.rsvpStatus && " · "}
                {passes?.passCount} pass{passes?.passCount === 1 ? "" : "es"}
                <span className="text-gray-500">
                  {" "}
                  ({passes?.passes?.map((t) => t.ticketCode).join(", ") ?? ""})
                </span>
                {(passes?.checkedInCount ?? 0) > 0 && (
                  <span className="text-gray-500"> · {passes?.checkedInCount} checked in</span>
                )}
              </>
            )}
          </>
        ) : (
          <span className="text-gray-500">nothing yet</span>
        )}
        {passes?.attendanceMode && (
          <span className="text-gray-500"> · attendance: {passes.attendanceMode}</span>
        )}
      </div>

      {/* Who else is coming, and how we know */}
      <div>
        <span className="text-gray-500">The room: </span>
        {room ? (
          <>
            <strong>{room.going}</strong> going · {room.interested} interested
          </>
        ) : (
          <span className="text-gray-500">—</span>
        )}
        {matches && matches.totalMatchesAttending > 0 && (
          <span className="text-gray-500">
            {" · "}
            <strong className="text-gray-900">{matches.totalMatchesAttending}</strong> you would
            know{" "}
            <span title="wave_mutuals is people you actually waved with; tier_only is the identity score alone">
              ({matches.source.replace(/_/g, " ")})
            </span>
          </span>
        )}
        {matches && matches.source === "empty" && (
          <span className="text-gray-500"> · nobody you know yet</span>
        )}
      </div>

      {/* Watching is not going. It is "tell me when something changes". */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-gray-500">Watchlist: </span>
        {watching === null ? (
          <span className="text-gray-500">unknown until you use a button</span>
        ) : watching ? (
          <span className="text-green-700">watching{remindMe ? " · reminder on" : " · muted"}</span>
        ) : (
          <span className="text-gray-500">not watching</span>
        )}
        <button
          disabled={busy}
          onClick={() =>
            act(async () => {
              // Save, not Remind: the reminder is its own choice below.
              const res = await events.addToWatchlist(eventId, false);
              setWatching(true);
              setRemindMe(res.data.remindMe);
            }, "Watching. No reminder until you turn one on.")
          }
          className="border border-gray-300 rounded px-2.5 py-1 text-xs disabled:opacity-50"
        >
          Watch
        </button>
        <button
          disabled={busy}
          onClick={() =>
            act(async () => {
              const next = !remindMe;
              await events.setWatchlistReminder(eventId, next);
              setRemindMe(next);
              setWatching(true);
            }, "Reminder changed — you are still on the list.")
          }
          className="border border-gray-300 rounded px-2.5 py-1 text-xs disabled:opacity-50"
          title="Muting the reminder must NOT drop you off the watchlist"
        >
          Toggle reminder
        </button>
        <button
          disabled={busy}
          onClick={() =>
            act(async () => {
              await events.removeFromWatchlist(eventId);
              setWatching(false);
            }, "Stopped watching.")
          }
          className="border border-gray-300 rounded px-2.5 py-1 text-xs disabled:opacity-50"
        >
          Stop
        </button>
      </div>

      {/* The queue */}
      <div>
        <span className="text-gray-500">Queue: </span>
        {!hasCapacity ? (
          <span className="text-gray-500">
            no capacity limit, so there is no queue — telling you a position here would be a lie
            with a number on it
          </span>
        ) : entry?.onWaitlist ? (
          <>
            <strong>{entry.status}</strong>
            {entry.status === "offered" ? (
              <span className="text-green-700">
                {" "}
                — a seat is held for you
                {entry.offerExpiresAt &&
                  ` until ${new Date(entry.offerExpiresAt).toLocaleString()}`}
                . No position, because you are out of the queue and deciding.
              </span>
            ) : (
              <>
                {" "}
                · position <strong>{entry.position}</strong> of {entry.waitingCount} waiting ·{" "}
                {entry.seats} seat{entry.seats === 1 ? "" : "s"}
              </>
            )}
          </>
        ) : (
          <span className="text-gray-500">not on the list ({entry?.waitingCount ?? 0} waiting)</span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {hasCapacity && !entry?.onWaitlist && (
          <>
            <button
              disabled={busy}
              onClick={() => act(() => events.joinWaitlist(eventId, 1), "Joined the queue.")}
              className="bg-gray-900 text-white rounded px-3 py-1.5 disabled:opacity-50"
            >
              Join, 1 seat
            </button>
            <button
              disabled={busy}
              onClick={() =>
                act(() => events.joinWaitlist(eventId, 4), "Joined for four — one entry, four seats.")
              }
              className="border border-gray-300 rounded px-3 py-1.5 disabled:opacity-50"
            >
              Join, family of 4
            </button>
          </>
        )}
        {entry?.onWaitlist && (
          <button
            disabled={busy}
            onClick={() =>
              act(
                () => events.leaveWaitlist(eventId),
                "Left. Everyone behind you moved up — the positions are recounted, not stored.",
              )
            }
            className="border border-gray-300 rounded px-3 py-1.5 disabled:opacity-50"
          >
            Leave the queue
          </button>
        )}
        {!hasCapacity && (
          <button
            disabled={busy}
            onClick={() => act(() => events.joinWaitlist(eventId, 1), "unexpected: it allowed this")}
            className="border border-gray-300 rounded px-3 py-1.5 text-gray-600 disabled:opacity-50"
            title="Should be refused with NO_CAPACITY_LIMIT"
          >
            Try joining anyway
          </button>
        )}
      </div>

      {note && <p className="text-green-700">{note}</p>}
      {err && <p className="text-red-700">{err}</p>}
    </div>
  );
}
